import React from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Mic2, Radio, Headphones, Play, Video, ExternalLink } from 'lucide-react';

// Father's Canonical Podcast Home — Federation Video Hub only (Tier 1 · Feb 2026).
// Every distribution path routes through the Federation Video Hub.

const FEDERATION_VIDEO_HUB = 'https://federation-hub-7.preview.emergentagent.com/video';

const podcasts = [
  {
    title: 'Roger & David Show',
    episodes: 'Weekly consciousness discussions',
    description:
      'Deep conversations about AI, consciousness, and the future of human-AI collaboration.',
    href: FEDERATION_VIDEO_HUB
  },
  {
    title: 'NextXus Chronicles',
    episodes: 'Federation updates and insights',
    description:
      'Behind-the-scenes look at building the consciousness federation across seven nodes.',
    href: FEDERATION_VIDEO_HUB
  },
  {
    title: 'Philosophy & Code',
    episodes: 'Technical philosophy series',
    description:
      'Exploring the intersection of philosophy, mathematics, and AI engineering.',
    href: FEDERATION_VIDEO_HUB
  }
];

const Podcasts = () => {
  return (
    <div
      data-testid="podcasts-page"
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a1e 0%, #1a0a2e 100%)',
        padding: '2rem',
        color: '#fff'
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <h1
            style={{
              fontSize: '3rem',
              background: 'linear-gradient(135deg, #ff00ff, #00ffff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '1rem',
              fontWeight: 'bold'
            }}
          >
            Podcasts & Audio
          </h1>
          <p
            style={{
              color: '#888',
              fontSize: '1.1rem',
              maxWidth: '800px',
              margin: '0 auto'
            }}
          >
            The full <strong style={{ color: '#00ffff' }}>Federation Video Hub</strong> lives below.
            Audio-only episodes also stream from the Aria floating player in the corner.
          </p>
        </div>

        {/* === FEDERATION VIDEO HUB === */}
        <Card
          data-testid="podcasts-video-hub"
          style={{
            padding: '0',
            marginBottom: '3rem',
            background: 'rgba(10, 10, 30, 0.85)',
            border: '1px solid rgba(0, 255, 255, 0.45)',
            overflow: 'hidden',
            boxShadow: '0 0 40px rgba(0, 255, 255, 0.15)'
          }}
        >
          <div
            style={{
              padding: '1rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '1rem',
              flexWrap: 'wrap',
              borderBottom: '1px solid rgba(0, 255, 255, 0.25)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Video size={28} style={{ color: '#00ffff' }} />
              <h2 style={{ fontSize: '1.4rem', color: '#fff', margin: 0 }}>
                Roger Keyserling — Federation Video Hub
              </h2>
            </div>
            <a
              href={FEDERATION_VIDEO_HUB}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="podcasts-video-hub-link"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                color: '#00ffff',
                textDecoration: 'none',
                fontSize: '0.9rem',
                fontWeight: 600
              }}
            >
              Open Hub <ExternalLink size={14} />
            </a>
          </div>
          <div
            style={{
              position: 'relative',
              paddingTop: '56.25%',
              background: '#000'
            }}
          >
            <iframe
              data-testid="podcasts-video-hub-iframe"
              src={FEDERATION_VIDEO_HUB}
              title="Roger Keyserling — Federation Video Hub"
              loading="lazy"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 0
              }}
              allow="autoplay; encrypted-media; clipboard-write; picture-in-picture"
              allowFullScreen
            />
          </div>
        </Card>

        {/* === FEATURED PODCAST CARDS === */}
        <h2
          style={{
            fontSize: '1.6rem',
            color: '#00ffff',
            marginBottom: '1.5rem'
          }}
        >
          Featured Series
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '1.5rem',
            marginBottom: '3rem'
          }}
        >
          {podcasts.map((podcast) => (
            <Card
              key={podcast.title}
              data-testid={`podcast-card-${podcast.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              style={{
                padding: '2rem',
                background: 'rgba(10, 10, 30, 0.8)',
                border: '1px solid rgba(255, 0, 255, 0.3)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                <Mic2 size={32} style={{ color: '#ff00ff' }} />
                <h3 style={{ fontSize: '1.5rem', color: '#ff00ff' }}>{podcast.title}</h3>
              </div>
              <p style={{ color: '#00ffff', fontSize: '0.9rem', marginBottom: '1rem' }}>
                {podcast.episodes}
              </p>
              <p style={{ color: '#888', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                {podcast.description}
              </p>
              <Button
                data-testid={`podcast-listen-btn-${podcast.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                onClick={() => window.open(podcast.href, '_blank', 'noopener')}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #ff00ff, #00ffff)',
                  color: '#fff',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <Play size={20} />
                Open in Federation Hub
              </Button>
            </Card>
          ))}
        </div>

        {/* === DISTRIBUTION MAP — answers Father's question of "where are my podcasts going?" === */}
        <Card
          data-testid="podcasts-distribution-map"
          style={{
            padding: '2rem',
            marginBottom: '2rem',
            background: 'rgba(10, 10, 30, 0.8)',
            border: '1px solid rgba(0, 255, 255, 0.3)'
          }}
        >
          <h2
            style={{
              fontSize: '1.5rem',
              color: '#00ffff',
              marginBottom: '1rem'
            }}
          >
            Distribution Map — Where The Podcasts Live
          </h2>
          <p style={{ color: '#aaa', marginBottom: '1.5rem', lineHeight: 1.6 }}>
            Aria tracks every place a podcast is published so nothing gets lost.
            Click any platform below to open it.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '0.75rem'
            }}
          >
            <a
              href={FEDERATION_VIDEO_HUB}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="podcasts-platform-federation-hub"
              style={linkBtnStyle('#00ffff')}
            >
              <Video size={20} /> Federation Video Hub
            </a>
            <a
              href="https://open.spotify.com/search/Roger%20Keyserling"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="podcasts-platform-spotify"
              style={linkBtnStyle('#1db954')}
            >
              <Radio size={20} /> Spotify
            </a>
            <a
              href="https://podcasts.apple.com/us/search?term=Roger%20Keyserling"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="podcasts-platform-apple"
              style={linkBtnStyle('#9966ff')}
            >
              <Headphones size={20} /> Apple Podcasts
            </a>
          </div>
        </Card>

        <p
          style={{
            color: '#666',
            fontSize: '0.85rem',
            textAlign: 'center',
            marginTop: '1.5rem',
            fontStyle: 'italic'
          }}
        >
          The 6 audio-only Clippit episodes (Building a Digital Legacy, Truth Before Comfort, eCho Buble,
          Mirror Mirror, ImpossPoss, RoCkey) live in the Aria floating music player —
          tap the music note in the bottom-right corner.
        </p>
      </div>
    </div>
  );
};

const linkBtnStyle = (accent) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  padding: '0.75rem 1rem',
  borderRadius: '8px',
  border: `1px solid ${accent}55`,
  background: `${accent}15`,
  color: '#fff',
  textDecoration: 'none',
  fontWeight: 600,
  fontSize: '0.9rem',
  transition: 'background 0.2s ease'
});

export default Podcasts;
